import { Alert, Button, Card, Input, Space, Typography, message } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Dialog as ZhDialog, GridItem as ZhGridItem } from "zhihao-ui-react";

import request from "@/api";
import type { AiCorpusPublishRow } from "./AiCorpusPublishPage";

interface CorpusInput {
    id?: number | string;
    content: string;
}

export interface AiCorpusPublishFormPayload {
    cropusList: string[];
    publishType: number;
}

export interface AiCorpusPublishFormPageProps {
    loadDetail?: (id: string) => Promise<AiCorpusPublishRow>;
    create?: (payload: AiCorpusPublishFormPayload) => Promise<unknown>;
    edit?: (id: number, payload: AiCorpusPublishFormPayload) => Promise<unknown>;
}

const createEmptyInputs = (): CorpusInput[] => Array.from({ length: 10 }, () => ({ content: "" }));
const MAX_BATCH_CORPUS_LENGTH = 35;

/** 复用 Vue AI语料发布详情接口，用于编辑回显。 */
export async function loadAiCorpusPublishFormDetail(id: string): Promise<AiCorpusPublishRow> {
    const response = await request.service.get<AiCorpusPublishRow>(`/corpus-publish/vo/${id}`);
    return response.data;
}

/** 复用 Vue AI语料发布新增接口。 */
export async function createAiCorpusPublish(payload: AiCorpusPublishFormPayload): Promise<unknown> {
    const response = await request.service.post("/corpus-publish", payload);
    return response.data;
}

/** 复用 Vue AI语料发布编辑接口。 */
export async function editAiCorpusPublish(id: number, payload: AiCorpusPublishFormPayload): Promise<unknown> {
    const response = await request.service.put(`/corpus-publish/${id}`, payload);
    return response.data;
}

/** AI语料发布新增/编辑表单，保留 Vue 的数量校验和 payload 字段名。 */
export function AiCorpusPublishFormPage({
    loadDetail = loadAiCorpusPublishFormDetail,
    create = createAiCorpusPublish,
    edit = editAiCorpusPublish
}: AiCorpusPublishFormPageProps) {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [inputs, setInputs] = useState<CorpusInput[]>(createEmptyInputs);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [batchImportVisible, setBatchImportVisible] = useState(false);
    const [batchCorpus, setBatchCorpus] = useState("");
    const [batchErrorMessage, setBatchErrorMessage] = useState<string | null>(null);
    // 保存待确认的发布类型，复刻 Vue ZhMessageBox 的二次确认而不提前调用写接口。
    const [confirmPublishType, setConfirmPublishType] = useState<number | null>(null);

    useEffect(() => {
        // 进入表单时清理其他页面遗留的全局提示，保证本页反馈可被准确识别。
        message.destroy();
    }, []);

    useEffect(() => {
        if (!id) return;
        let disposed = false;
        void loadDetail(id)
            .then(result => {
                if (disposed) return;
                const loaded = result.corpusList.map(item => ({
                    id: item.id,
                    content: (item as { content?: string }).content || ""
                }));
                setInputs(
                    loaded.length < 10
                        ? [...loaded, ...Array.from({ length: 10 - loaded.length }, () => ({ content: "" }))]
                        : loaded
                );
            })
            .catch(() => {
                if (!disposed) setErrorMessage("AI语料发布详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadDetail]);

    const updateInput = (index: number, content: string) => {
        setInputs(current => current.map((item, itemIndex) => (itemIndex === index ? { ...item, content } : item)));
    };

    /** 按 Vue 批量导入契约截断超长行，并反馈被截断的校验问题。 */
    const updateBatchCorpus = (value: string) => {
        const lines = value.split("\n");
        const hasOverlongLine = lines.some(line => line.length > MAX_BATCH_CORPUS_LENGTH);
        setBatchCorpus(lines.map(line => line.slice(0, MAX_BATCH_CORPUS_LENGTH)).join("\n"));
        setBatchErrorMessage(hasOverlongLine ? "每行语料不能超过35个字" : null);
    };

    /** 关闭批量导入弹窗时清空临时文本，不污染表单已填写内容。 */
    const closeBatchImport = () => {
        setBatchImportVisible(false);
        setBatchCorpus("");
        setBatchErrorMessage(null);
    };

    /** 过滤空行并将批量语料追加到已有非空输入框，随后补足十行。 */
    const submitBatchImport = () => {
        const importedLines = batchCorpus.split("\n").filter(line => line.trim() !== "");
        setInputs(current => {
            const existingInputs = current.filter(item => item.content !== "");
            const nextInputs = [...existingInputs, ...importedLines.map(content => ({ content }))];
            return nextInputs.length < 10
                ? [...nextInputs, ...Array.from({ length: 10 - nextInputs.length }, () => ({ content: "" }))]
                : nextInputs;
        });
        closeBatchImport();
    };

    const submit = async (publishType: number) => {
        const cropusList = inputs.map(item => item.content.trim()).filter(Boolean);
        if (publishType === 1 && cropusList.length < 10) {
            setErrorMessage("至少发布10条语料");
            return;
        }
        if (publishType === 0 && cropusList.length === 0) {
            setErrorMessage("请输入至少1条语料");
            return;
        }
        // 旧 Vue 在组装 payload 后先弹出 ZhMessageBox，确认后才执行新增/编辑接口。
        setConfirmPublishType(publishType);
    };

    const confirmSubmit = async () => {
        if (confirmPublishType == null) return;
        const publishType = confirmPublishType;
        const cropusList = inputs.map(item => item.content.trim()).filter(Boolean);
        const payload = { cropusList, publishType };
        setConfirmPublishType(null);
        try {
            if (id) await edit(Number(id), payload);
            else await create(payload);
            setErrorMessage(null);
            // 清理旧的全局提示，避免连续操作时历史 toast 干扰当前结果反馈。
            message.destroy();
            message.success(publishType === 1 ? "已发布" : "已保存");
        } catch {
            // 失败提示直接落在页面内，避免全局 toast 与其他操作结果重叠。
            message.destroy();
            setErrorMessage("AI语料发布保存失败");
        }
    };

    return (
        <section className="flex h-full min-h-full flex-col" aria-label="ai-corpus-publish-form-page">
            <Card>
                <Typography.Title level={4}>{id ? "编辑AI语料发布" : "新增AI语料发布"}</Typography.Title>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="warning" /> : null}
                <div className="max-w-2xl">
                    {inputs.map((item, index) => (
                        <Space key={`${item.id ?? "new"}-${index}`} className="mb-3 flex" align="center">
                            <Typography.Text className="w-16">语料{index + 1}</Typography.Text>
                            <Input
                                aria-label={`语料输入 ${index + 1}`}
                                maxLength={35}
                                placeholder="请输入语料"
                                value={item.content}
                                onChange={event => updateInput(index, event.target.value)}
                            />
                            {index > 9 ? (
                                <Button
                                    aria-label={`删除语料 ${index + 1}`}
                                    onClick={() =>
                                        setInputs(current => current.filter((_, itemIndex) => itemIndex !== index))
                                    }
                                >
                                    删除
                                </Button>
                            ) : null}
                            {index === inputs.length - 1 ? (
                                <Button
                                    aria-label="添加语料"
                                    onClick={() => setInputs(current => [...current, { content: "" }])}
                                >
                                    添加
                                </Button>
                            ) : null}
                        </Space>
                    ))}
                </div>
                {/* 对齐 Vue 操作栏，将批量导入、发布、保存和返回集中放入四列网格项。 */}
                <ZhGridItem span={4}>
                    <Space className="operation-fixed-btn mt-6">
                        <Button aria-label="批量导入" type="primary" onClick={() => setBatchImportVisible(true)}>
                            批量导入
                        </Button>
                        <Button aria-label="发布" type="primary" onClick={() => void submit(1)}>
                            发布
                        </Button>
                        <Button aria-label="保存" onClick={() => void submit(0)}>
                            保存
                        </Button>
                        <Button aria-label="返回" onClick={() => void navigate(-1)}>
                            返回
                        </Button>
                    </Space>
                </ZhGridItem>
            </Card>
            <ZhDialog
                onCancel={closeBatchImport}
                open={batchImportVisible}
                title="批量导入"
                width={650}
                footer={
                    <Space>
                        <Button aria-label="取消" onClick={closeBatchImport}>
                            取消
                        </Button>
                        <Button aria-label="确认" type="primary" onClick={submitBatchImport}>
                            确认
                        </Button>
                    </Space>
                }
            >
                {batchErrorMessage ? (
                    <Alert className="mb-4" message={batchErrorMessage} role="alert" type="warning" />
                ) : null}
                <Input.TextArea
                    aria-label="批量语料"
                    maxLength={1000}
                    onChange={event => updateBatchCorpus(event.target.value)}
                    placeholder="请输入语料"
                    showCount
                    value={batchCorpus}
                />
                <Typography.Text type="secondary">输入示例：豫信货19777水尺是什么船，每行一个语料</Typography.Text>
            </ZhDialog>
            <ZhDialog
                open={confirmPublishType != null}
                title="提示"
                width={400}
                onCancel={() => setConfirmPublishType(null)}
                footer={
                    <Space>
                        <Button onClick={() => setConfirmPublishType(null)}>取消</Button>
                        <Button type="primary" onClick={() => void confirmSubmit()}>
                            确认
                        </Button>
                    </Space>
                }
            >
                <Typography.Text>
                    {confirmPublishType === 1 ? "是否发布，确认后将发布在企业微信的AI语料收集？" : "是否提交"}
                </Typography.Text>
            </ZhDialog>
        </section>
    );
}
